/**
 * PdfDrawingSheet — the NAVIGABLE viewer for PDF technical drawings (tavole)
 * inside the Tecnico hub's preview stage.
 *
 * Sep 2 2026 — before this, PDF tavole were locked to the static BE-rendered
 * PNG: no zoom, no pan, no page nav ("non posso fare zoom ed è bruttino").
 * DRY fix: reuse the SAME production ``PDFViewerInline`` (react-pdf: zoom
 * 0.5–3×, page ◀ N/M ▶, Fit, Download) the Hive-flow run pane mounts —
 * dispatched here for the ``pdf`` cad_format instead of ``CadViewerSwitch``
 * (which can't parse PDF bytes).
 *
 * Bytes come from ``useChatFileBlobUrl`` (auth-proxied blob) exactly like the
 * flow viewer — react-pdf's own fetch path can't carry the Bearer header.
 * The viewer is lazy-loaded so the Tecnico bundle stays lean and the module
 * graph never eagerly pulls the editors barrel (vitest CSS transform).
 */

import { Trans } from "@lingui/react/macro";
import { Loader2, TriangleAlert } from "lucide-react";
import * as React from "react";

import type { DxfFile } from "@ando/core";
import { readOnlyBlobSession } from "@ando/ui/editors/session";

import { useChatFileBlobUrl } from "@/features/chat/hooks/useChatFileBlobUrl";

const PDFViewerInline = React.lazy(() =>
  import("@ando/ui/editors").then((m) => ({ default: m.PDFViewerInline })),
);

/** The two fields the sheet actually reads — structurally satisfied by a
 *  full ``DxfFile`` (Tecnico hub) AND by the chat CAD-result panel's
 *  ``source_files_info`` entry, so both surfaces share one viewer. */
export type PdfDrawingRef = Pick<DxfFile, "content_item_id" | "filename">;

export function PdfDrawingSheet({ file }: { readonly file: PdfDrawingRef }) {
  const { url, isLoading, isError } = useChatFileBlobUrl(file.content_item_id);

  // Centred status states — a glass-dark pill (same recipe as the stage's
  // floating action bar) so the copy reads on BOTH mounts: the dark
  // drafting-table canvas (Tecnico hub) and the light chat result panel.
  const status = (child: React.ReactNode) => (
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="inline-flex items-center gap-2 rounded-lg bg-black/45 px-3 py-1.5 text-[13px] text-white/85 backdrop-blur-md">
        {child}
      </span>
    </div>
  );

  if (isLoading || (!url && !isError)) {
    return status(
      <>
        <Loader2 size={15} aria-hidden strokeWidth={2} className="animate-spin text-ando-orange" />
        <Trans>Carico la tavola…</Trans>
      </>,
    );
  }
  if (isError || !url) {
    return status(
      <>
        <TriangleAlert size={15} aria-hidden strokeWidth={2} className="text-red-400" />
        <Trans>Impossibile caricare la tavola.</Trans>
      </>,
    );
  }

  const session = readOnlyBlobSession(url, file.filename, "application/pdf");
  return (
    <div className="absolute inset-0 overflow-hidden">
      <React.Suspense
        fallback={status(
          <>
            <Loader2
              size={15}
              aria-hidden
              strokeWidth={2}
              className="animate-spin text-ando-orange"
            />
            <Trans>Carico la tavola…</Trans>
          </>,
        )}
      >
        <PDFViewerInline session={session} />
      </React.Suspense>
    </div>
  );
}
PdfDrawingSheet.displayName = "PdfDrawingSheet";
